import AddIcon from '@mui/icons-material/Add';
import DeleteIconButton from '../../components/DeleteIconButton';
import { Autocomplete, Button, Checkbox, CircularProgress, FormControlLabel, Grid, InputAdornment, Link, Switch, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import MaterialUiPhoneNumber from 'material-ui-phone-number';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useDropzone } from 'react-dropzone';

import { CREATE, DataAPIContext, GET_LIST } from '../../api';
import BlockRedirect from '../../components/BlockRedirect';
import FileActionRow from '../../components/FileActionRow';
import BookingTickets from './BookingTickets';
import FormActionButtons from '../../components/FormActionButtons';
import RemoteAutocomplete from '../../components/RemoteAutocomplete';
import { ContextStore } from '../../store/contextStore';
import { validateEmail } from '../../utils';
import { convertToAmPm, isUSLocale } from '../../utils/date';
import fileToSha1String from '../../utils/file_to_sha1';
import { hasInvisibleChars } from '../../utils/unicode_chars';
import { tourUsesOperatorDefaults } from '../../utils/tagCategories';
import { extractStartAndEndDate } from '../utils';
import UserRemoteAutocomplete from './AddUserRemoteAutoComplete';
import CheckoutPicker from './CheckoutPicker';
import { checkoutForNewTripDate, tripDateMidnight } from './checkoutTime';
import { blank_trip_user, checkRequiredTagsSelected, compare, ERROR_MESSAGES, filteredMaxedTags, mapped_user_roles, validateUser } from './utils';

// Group_Tour_User.END_TS_TYPE_* (mirrors TripUserEdit.js). The server derives the
// discriminator from whether end_ts is sent on create, so the form tracks end_ts_type
// only to drive rendering.
const END_TS_TYPE_ITINERARY = 0;
const BOOKING_OWN_END = 1;

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.paper,
        margin: theme.spacing(1),
    },
    addFormRoot: {
        maxWidth: '40vw',
        padding: '1rem',
        maxHeight: '45vh',
        overflowY: 'auto',

        [theme.breakpoints.down('sm')]: {
            maxWidth: '92vw',
            maxHeight: `calc(${theme.cp.contentHeight} - 5rem)`,
            overflowY: 'auto',
            marginTop: '5rem',
            border: 'solid 1px #d8d8d8',
        },
    },
}));



const AddTripUser = React.forwardRef((props, ref) => {
    const [tripUser, setTripUser] = React.useState(blank_trip_user);
    const dataAPI = React.useContext(DataAPIContext);
    const classes = useStyles();
    const { roleMap, hasPerm, tourOperator } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const requiresDepartureSelection = props.trip.has_departures && !props.trip.is_bespoke;
    const tripTimeZone = props.trip.start_timezone || 'UTC';

    // Merge trip-level categories with operator default categories (fresh from ContextStore),
    // but only when the trip actually uses operator defaults.
    const mergedTagCategories = React.useMemo(() => {
        const tripCategories = props.trip.tag_categories || [];
        if (!tourUsesOperatorDefaults(props.trip, tourOperator)) {
            return tripCategories;
        }
        const operatorDefaults = tourOperator?.default_tag_categories || [];

        // Build a set of category IDs that exist at trip level
        const tripCategoryIds = new Set(
            tripCategories.filter(c => c.category?.id).map(c => c.category.id.toString())
        );

        // Add operator defaults that aren't already in trip categories
        const missingDefaults = operatorDefaults.filter(
            cat => !tripCategoryIds.has(cat.category?.id?.toString())
        );

        return [...tripCategories, ...missingDefaults];
    }, [props.trip, tourOperator]);

    const emailRef = React.useRef(null);
    const saveBtRef = React.useRef(null);
    const [phoneServerCheck, setPhoneServerCheck] = React.useState(false);
    const [serverCheck, setServerCheck] = React.useState(false);
    const [serverErrors, setServerErrors] = React.useState(null);
    const [clientErrors, setClientErrors] = React.useState({
        missingEmailAddress: false,
        existingTripUser: false,
        userNotFound: false,
        sameDateExists: false,
        validEmailAddress: false,
        userFoundBespoke: false,
        missingRequiredTags: false,
        missingDepartureTag: false,
        unicodeCharactersFound: false,
    });


    let [departureOptions, setDepartureOptions] = React.useState([]);
    const [departureOptionsLoaded, setDepartureOptionsLoaded] = React.useState(false);
    // A trip can inherit the operator's departure category without having any departure
    // times (a seeded "Blank tour" template, CP-7922): there is nothing to pick, so say why.
    const noDepartureTimes = requiresDepartureSelection && departureOptionsLoaded && departureOptions.length === 0;
    let missing_required_tags = !checkRequiredTagsSelected(null, tripUser)
    const { errors, canSave } = validateUser(tripUser, serverErrors, requiresDepartureSelection, missing_required_tags);
    const allowedTagCategories = filteredMaxedTags(mergedTagCategories, tripUser.tags);

    const [existingUserMatch, setExistingUserMatch] = React.useState(null);
    const nonBespokeStartDate = extractStartAndEndDate(props.trip.schedule)?.startDate
    const nonBespokeEndDate = extractStartAndEndDate(props.trip.schedule)?.endDate

    const saveEnabled = Object.keys(clientErrors).filter((ce) => clientErrors[ce]).length === 0;
    // A booking that opted into its own end must actually carry one, or the create would
    // silently fall back to an itinerary end (server derives the type from end_ts presence).
    const ownEndIncomplete = tripUser.end_ts_type === BOOKING_OWN_END && !tripUser.end_ts;
    const disableSaveButton = !saveEnabled || !canSave || (!props.trip.is_bespoke && !tripUser.date) || ownEndIncomplete;
    const hasWritePerm = hasPerm('tour_participant', 'write')
    // CP-8190: without user.list the user grid endpoint returns empty results, so
    // fall back to the tour_participant.write-gated exact-email lookup instead.
    const canBrowseUsers = hasPerm('user', 'list')




    // add required categories as items in tags array
    React.useEffect(() => {
        let updateTagsArray = [];
        // departure tags are handled separately in tripUser.departure
        let requiredCategories = mergedTagCategories.filter((cat) => cat.min_tags > 0 && cat.category.code !== 'departure');

        for (let i = 0; i < requiredCategories.length; i++) {
            let newTag = { idx: i, category: requiredCategories[i].category, _changed: false };
            updateTagsArray.push(newTag);
        }
        setTripUser((prevState) => ({ ...prevState, tags: updateTagsArray }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergedTagCategories]);

    React.useEffect(() => {
        const fetchDepartureOptions = async () => {
            let params = {
                filter: {
                    query: null,
                    filters: JSON.stringify([{ field: "category_code", op: "eq", value: "departure" }, { field: "tour_id", op: "eq" }]),
                },
                urlSuffix: `${props.trip.id}/departures/`,
            };
            const res = await dataAPI(GET_LIST, 'tour', params);
            if (res.data) {
                let res_data = res.data.sort((a, b) => {
                    // Direct string comparison works because the time format is HH:mm
                    return a?.tag?.name?.localeCompare(b.tag.name);
                });
                setDepartureOptions(res_data);
                setDepartureOptionsLoaded(true);
            }
        };
        fetchDepartureOptions();
    }, []);

    const formatTimeValue = (value) => {
        if (isUSLocale()) {
            return convertToAmPm(value)
        }
        return value;
    }


    const onStringChange = (ev) => {
        let new_string = ev.target.value;
        setTripUser((prev) => ({ ...prev, user: { ...prev.user, [ev.target.name]: new_string }, _changed: true }));
        if (ev.target.name === 'email') {
            if (validateEmail(new_string.toLowerCase())) {
                setClientErrors((prev) => ({ ...prev, validEmailAddress: false, missingEmailAddress: false }));
            }
            if (hasInvisibleChars(new_string)) {
                setClientErrors((prev) => ({ ...prev, unicodeCharactersFound: true }));
            } else {
                setClientErrors((prev) => ({ ...prev, unicodeCharactersFound: false }));
            }
            emailRef.current = new_string;
        }
    };

    const onTextChange = (ev) => {
        setTripUser((prev) => ({...prev, [ev.target.name]: ev.target.value, _changed: true}));
    }

    const onCheckboxChange = (ev) => {
        setTripUser((prev) => ({...prev, [ev.target.name]: ev.target.checked, _changed: true}));
    };

    const switchToExistingUser = (existing_user) => {
        if (existing_user) {
            setClientErrors((prev) => ({
                ...prev,
                userNotFound: false,
            }));
            setTripUser((prev) => ({ ...prev, user: existing_user, is_existing: true, role: existing_user.roles.length > 0 ? existing_user.roles[0] : null }));
        } else {
            setTripUser((prev) => ({ ...prev, is_existing: true }));
        }
        setServerErrors(null);
        setClientErrors((prev) => ({ ...prev, validEmailAddress: false, missingEmailAddress: false }));
    };

    const onExistingUserSelect = async (ev, nv) => {
        if (!nv) {
            return;
        }
        if (nv) {
            const tlist = tripUser.tags.filter((x) => x.category.code === 'departure');
            const deptag = requiresDepartureSelection && tlist.length > 0 ? tlist[0] : null;
            await checkIfAlreadyExistingTripUser(nv.email, nv.date, deptag);
            setClientErrors((prev) => ({ ...prev, userNotFound: false }));
        }
        setTripUser((prev) => ({ ...prev, user: nv, role: nv.roles.length > 0 ? nv.roles[0] : null }));
        setServerErrors(null);
    };

    const switchToNewUser = async (ev) => {
        setTripUser((prev) => ({
            ...prev, is_existing: false, is_staff: false, role: null, user: { ...blank_trip_user.user, email: prev.user.email }
        }))
        //It takes the UI a few hundred milliseconds to switch to New user before removing the previous errors
        setTimeout(() => {
            setClientErrors((prev) => ({
                ...prev,
                userNotFound: false,
                existingTripUser: false,
                userFoundBespoke: false,
            }));
        }, 1000);
    };

    const onRootKeyUp = (ev) => {
        if (ev.keyCode === 27) {
            onCancel();
        }
    };

    const onStringBlur = (ev) => {
        if (ev.target.name === 'email') {
            if (validateEmail(ev.target.value)) {
                setClientErrors((prev) => ({ ...prev, validEmailAddress: false }));
            } else {
                setClientErrors((prev) => ({ ...prev, validEmailAddress: true }));
            }
        }
    };

    const onPhoneChange = (phone, details) => {
        setTripUser((prev) => ({ ...prev, user: { ...prev.user, phone: phone } }));
    };

    const onDateChange = (nd) => {
        if (!nd) {
            setTripUser((prev) => ({ ...prev, date: null }));
            return;
        }
        if (moment.isMoment(nd)) {
            nd = moment(nd).utc(true).toDate();
        }

        const deptag = tripUser.departure?.tag;
        setServerCheck(true);
        checkIfAlreadyExistingTripUser(tripUser.user?.email, moment.utc(nd).format('yyyy-MM-DD'), deptag)
            .then((res) => {
                if (!res.response) {
                    setClientErrors((prev) => ({
                        ...prev,
                        sameDateExists: false,
                    }));
                }
            })
            .finally(() => {
                setServerCheck(false);
                const date = moment.utc(nd).format('yyyy-MM-DD');
                setTripUser((prevState) => ({
                    ...prevState,
                    date,
                    // The check-out follows the trip date (CP-8392).
                    ...(prevState.end_ts_type === BOOKING_OWN_END
                        ? { end_ts: checkoutForNewTripDate(prevState.end_ts, prevState.date, date, tripTimeZone) }
                        : {}),
                }));
            });
    };


    const onDepartureChange = async (ev, nt) => {
        let newDepartureTag = nt.tag;
        setServerCheck(true);
        let check_res = await checkIfAlreadyExistingTripUser(tripUser.user?.email, tripUser.date, newDepartureTag);
        setServerCheck(false);
        let tags_to_check_if_required = [];
        if (!check_res.response) {
            setTripUser((prevState) => {
                return { ...prevState, departure: nt }
            });
        } else {
            enqueueSnackbar('Same user is already registered at the selected departure time', { variant: 'error' });
        }
        checkRequiredTagsSelected(tags_to_check_if_required, tripUser, setClientErrors);
    };

    const onRoleChange = (ev, role) => {
        if (!role) {
            return;
        }
        setTripUser((prev) => ({ ...prev, role: parseInt(role?.id) }));
    };

    const handleUserRemoteAutoCompleteOnBlur = (ev, user) => {
        const tlist = tripUser.tags.filter((x) => x?.category?.code === 'departure');
        const deptag = requiresDepartureSelection && tlist.length > 0 ? tlist[0] : null;
        checkIfAlreadyExistingTripUser(ev.target.value, tripUser.date, deptag);
    };

    const addNewEmptyTag = (ev) => {
        ev.stopPropagation();
        setTripUser((prevState) => {
            let newUserTourTags = [...prevState.tags, { idx: prevState.length + 1 }];
            // generate new idx for each tag
            for (let i = 0; i < newUserTourTags.length; i++) {
                newUserTourTags[i].idx = i;
            }
            return { ...prevState, tags: [...newUserTourTags] };
        });
    };

    const removeTagFromUser = (ev, idx) => {
        setTripUser((prevState) => {
            let newUserTourTags = prevState.tags.filter((tag) => tag.idx !== idx);
            // generate new idx for each tag
            for (let i = 0; i < newUserTourTags.length; i++) {
                newUserTourTags[i].idx = i;
            }
            return { ...prevState, tags: [...newUserTourTags] };
        });
    };

    const handleTagCategoryChange = (ev, cat, idx) => {
        if (!cat) {
            return;
        }
        ev.stopPropagation();
        setTripUser((prevState) => {
            let prev_tags = [...prevState.tags];
            let existing_tags = [...prev_tags.filter((tag) => tag.idx !== idx)];
            existing_tags = [...existing_tags, { idx: idx, category: cat.category }].sort(compare);
            return { ...prevState, tags: [...existing_tags] };
        });
    };

    const handleTagChange = (ev, newTag, idx) => {
        ev.stopPropagation();
        if (!newTag) {
            setTripUser((prevState) => {
                let prev_tags = [...prevState.tags];
                let existing_tags = [...prev_tags.filter((tag) => tag.idx !== idx)];
                let selected_tag = [...prev_tags.filter((tag) => tag.idx === idx)][0];
                return { ...prevState, tags: [...existing_tags, { idx: selected_tag.idx, category: selected_tag.category }] };
            });
            return;
        }
        setTripUser((prevState) => {
            let prev_tags = [...prevState.tags];
            checkRequiredTagsSelected([...prev_tags.filter((tag) => tag.idx !== idx), { ...newTag, idx: idx }], tripUser, setClientErrors);
            let existing_tags = [...prev_tags.filter((tag) => tag.idx !== idx).sort(compare)];
            return { ...prevState, tags: [...existing_tags, { ...newTag, idx: idx }] };
        });
    };

    // Same contract as TripUserEdit: CheckoutPicker hands back the ISO instant
    // the operator picked on the tour's clock.
    const onEndTsChange = (endTs) => {
        setTripUser((prev) => ({
            ...prev,
            end_ts: endTs,
            _changed: true,
        }));
    };

    // Opt this new booking into its own check-out time (a hotel stay). On: reveal the
    // picker, prefilled with midnight of the trip date on the tour's clock (midnight UTC
    // showed as the day before on a US tour); the operator sets the time. Off: no end_ts is sent, so the booking derives its end from the tour. The
    // server owns end_ts_type -- it is derived from end_ts presence on create -- so we
    // set end_ts_type locally only to drive rendering.
    const onOwnEndToggle = (ev) => {
        if (ev.target.checked) {
            setTripUser((prev) => ({
                ...prev,
                end_ts_type: BOOKING_OWN_END,
                end_ts: prev.end_ts || (prev.date ? tripDateMidnight(prev.date, tripTimeZone).toISOString() : null),
                _changed: true,
            }));
        } else {
            setTripUser((prev) => ({
                ...prev,
                end_ts_type: END_TS_TYPE_ITINERARY,
                end_ts: null,
                _changed: true,
            }));
        }
    };

    const onAddUser = (ev) => {
        if (checkRequiredTagsSelected(null, tripUser, setClientErrors)) {
            // setAttribute / removeAttribute work instantly - unlike React state updates
            if (saveBtRef && saveBtRef.current) {
                saveBtRef.current.setAttribute('disabled', 'disabled');
            }
            // Ensure new state reflects in blockRedirect component to prevent leave prompt 
            setTripUser(prev => ({ ...prev, _changed: false }));
            setTimeout(() => {
                props.onAdd && props.onAdd({ ...tripUser });
            }, 300);
        }
    };

    // Confirms discarding unsaved changes (if any) before actually closing.
    // Exposed via ref so callers outside this component (e.g. click-away on the
    // containing Popper) go through the same guard instead of closing unconditionally.
    const requestClose = () => {
        if (tripUser._changed && !window.confirm('Are you sure you want to leave without saving changes?')) {
            return false;
        }
        props.onCancel && props.onCancel(true);
        return true;
    };

    React.useImperativeHandle(ref, () => ({ requestClose }));

    const onCancel = (ev) => {
        requestClose();
    };

    const checkIfRequiredAndMandatory = (tag) => {
        let result = false;
        // get the associated category from the merged categories (trip + operator defaults) for the properties(min/max_tags, chat, location etc..)
        let required_associated_tag_category = mergedTagCategories.filter((cat) => cat.category.id === tag.category?.id)[0];
        // check if required and if there is at least one more already same category required existing tag and that is first or not(sorted by idx)
        if (required_associated_tag_category?.min_tags > 0 && tripUser.tags.filter((utt) => utt?.category?.id === required_associated_tag_category?.category?.id && utt.idx < tag.idx).length === 0) {
            result = true;
        }
        return result;
    };


    // existing user check
    React.useEffect(() => {
        const tm = setTimeout(async () => {
            if (!tripUser.user.email || tripUser.user.email.length === 0) {
                return;
            }

            // CP-8190: exact-match lookup gated on tour_participant.write instead of the
            // user grid endpoint — that one silently returns nothing without user.list,
            // making this check misreport existing users as new.
            const params = {
                filter: { query: tripUser.user.email.toLowerCase(), tour_id: props.trip.id },
                urlSuffix: 'lookup_existing/',
            };
            if (!tripUser.is_existing) {
                setServerCheck(true);
                await dataAPI(GET_LIST, 'tour_user', params)
                    .then((res) => {
                        // exact match only; may also match by username/login, so don't re-filter on email
                        let exact_email_address_found = res?.data?.[0];
                        if (exact_email_address_found) {
                            if (!tripUser.is_existing) {
                                setExistingUserMatch(exact_email_address_found);
                                setServerErrors(prev => ({
                                    ...prev,
                                    email: {
                                        error: true,
                                        helperText: (
                                            <span>
                                                User already exists.
                                                <Link href={undefined} style={{ cursor: 'pointer' }} onClick={() => switchToExistingUser(exact_email_address_found)}>
                                                    Switch to existing user
                                                </Link>
                                            </span>
                                        ),
                                    },
                                }));
                            }
                        } else {
                            setExistingUserMatch(null);
                            const nse = { ...serverErrors };
                            delete nse.email;
                            setServerErrors(null);
                        }
                    })
                    .finally(() => {
                        setServerCheck(false);
                    });
            }
        }, 1000);
        return () => {
            clearTimeout(tm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripUser.is_existing, tripUser.user.email]);

    // phone input server validation
    React.useEffect(() => {
        const tm = setTimeout(async () => {
            const params = {
                data: { phone: tripUser.user.phone },
                urlSuffix: 'validate_phone/',
            };
            const testIfAreaCode = (area_code) => {
                let res = /^(\+?\d{1,3}|\d{1,4})$/;
                if (area_code === '+') {

                    setServerErrors((prev) => ({
                        ...prev,
                        phone: {
                            error: false,
                            helperText: '',
                        },
                    }));
                    return true;
                }
                if (res.test(area_code)) {
                    setServerErrors((prev) => ({
                        ...prev,
                        phone: {
                            error: false,
                            helperText: '',
                        },
                    }));

                    // setClientErrors((prev) => ({
                    //     ...prev,
                    //     phoneServerCheckError: false,
                    // }));
                }
                return res.test(area_code);
            };
            if (!testIfAreaCode(tripUser.user.phone) && tripUser.user.phone !== '') {
                setPhoneServerCheck(true);
                await dataAPI(CREATE, 'user', params)
                    .then((res) => {
                        if (res.data && !res.data.status && !tripUser.is_existing) {
                            setServerErrors((prev) => ({
                                ...prev,
                                phone: {
                                    error: true,
                                    helperText: ERROR_MESSAGES['phoneServerCheckError'],
                                },
                            }));
                        } else {
                            setServerErrors((prev) => ({
                                ...prev,
                                phone: {
                                    error: false,
                                    helperText: '',
                                },
                            }));
                        }
                    })
                    .finally(() => {
                        setPhoneServerCheck(false);
                    });
            }
        }, 1000);
        return () => {
            clearTimeout(tm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripUser.user.phone]);



    const checkIfAlreadyExistingTripUser = async (email_address, date, deptag) => {
        // check for existing tour user and same date for non-bespoke trips
        let returned_users = [];
        let response = false;
        if (!email_address) {
            return { response, returned_users };
        }

        if (requiresDepartureSelection && !deptag) {
            return { response, returned_users };
        }
        if (!props.trip.is_bespoke && !date) {
            return { response, returned_users };
        }

        const filters = [
            { field: 'email', value: email_address },
            { field: 'date', value: date && date.constructor !== String ? format(date, 'yyyy-MM-dd') : date },
        ];
        if (requiresDepartureSelection) {
            filters.push({ field: 'departure', value: deptag.id });
        }

        let params = {
            filter: {
                filters: JSON.stringify(filters),
            },
            urlSuffix: `?tour_id=${props.trip.id}`,
        };
        setServerCheck(true);
        await dataAPI(GET_LIST, `tour_user`, params)
            .then((res) => {
                if (res.data.length > 0) {
                    response = true;
                    returned_users = res.data;
                    if (res.data.filter((user) => ((!requiresDepartureSelection || user.departure.name === deptag.name) && user.user.email === email_address).length > 0)) {
                        setClientErrors((prev) => ({
                            ...prev,
                            sameDateExists: !props.trip.is_bespoke,
                        }));
                    }
                } else {
                    setClientErrors((prev) => ({
                        ...prev,
                        sameDateExists: false,
                    }));
                }
            })
            .finally(setServerCheck(false));

        if (props.trip.is_bespoke && response) {
            setClientErrors((prev) => ({ ...prev, userFoundBespoke: true }));
        } else {
            setClientErrors((prev) => ({ ...prev, userFoundBespoke: false }));
        }

        return { response, returned_users };
    };

    const onPhotoSharingLinkChange = (ev) => {
        setTripUser((prev) => ({ ...prev, _changed: true, tag_context: { ...prev.tag_context, photo_sharing: ev.target.value } }));
    };

    // file Attachments
    // CP-5559: the idx of the attachment a Replace icon opened the picker for; null when Add attachment did.
    const replaceAttachmentIdx = React.useRef(null);
    const onDrop = React.useCallback(async (acceptedFiles) => {
        let file = acceptedFiles[0];
        if (!file) return;
        const replaceIdx = replaceAttachmentIdx.current;
        let hash_string = await fileToSha1String(file);
        setTripUser((prev) => {
            const others = prev.attachments.filter((existing_file) => existing_file.idx !== replaceIdx);
            if (others.filter((existing_file) => existing_file.hash === hash_string).length > 0) {
                enqueueSnackbar('Duplicate file found, please check existing attachments', { variant: 'default', autoHideDuration: 3000 });
                return { ...prev, attachments: [...prev.attachments] };
            } else if (replaceIdx !== null) {
                return { ...prev, attachments: prev.attachments.map((existing_file) => (existing_file.idx === replaceIdx ? { file: file, idx: replaceIdx, hash: hash_string } : existing_file)), _changed: true };
            } else {
                // Not attachments.length: after a removal that repeats a live idx, and Remove/Replace act by idx.
                const idx = prev.attachments.reduce((max, existing_file) => Math.max(max, existing_file.idx), -1) + 1;
                return { ...prev, attachments: [...prev.attachments, { file: file, idx: idx, hash: hash_string }], _changed: true };
            }
        });
    }, []);

    const onVoucherDrop = React.useCallback(async (acceptedFiles) => {
        let file = acceptedFiles[0];
        let hash_string = await fileToSha1String(file);
        setTripUser((prev) => {
            return { ...prev, voucher: { file: file, name: file.name, content_hash: hash_string }, _changed: true };
        })
    }, []);

    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
    });

    const voucherDz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onVoucherDrop,
        multiple: false,
    });

    const handleFileDelete = (idx) => {
        setTripUser((prev) => {
            let file_attachments = prev.attachments.filter((file) => file.idx !== idx);
            return { ...prev, attachments: file_attachments, _changed: true };
        });
    };

    const handleVoucherDelete = (idx) => {
        setTripUser((prev) => {
            return { ...prev, voucher: null, _changed: true };
        });
    };

    const handleOnReplace = (dz, attachmentIdx = null) => {
        replaceAttachmentIdx.current = attachmentIdx;
        dz.open();
    };


    return (
        <div className={classes.addFormRoot} onKeyUp={onRootKeyUp}>
            <BlockRedirect
                shouldBlock={tripUser._changed}
                message='Are you sure you want to leave without saving changes?'
                ignoredPaths={['/trip']}
            />
            <Grid container direction="row" spacing={2}>
                <Grid item xs={6} container justifyContent="flex-start" alignItems="center">
                    <Typography variant="h5">{tripUser.is_existing ? 'Add Existing User' : 'Add New User'}</Typography>
                </Grid>
                <Grid item xs={6} container justifyContent="flex-end" alignItems="center">
                    <FormControlLabel
                        control={<Switch disabled={serverCheck} checked={tripUser.is_existing} onChange={() => (tripUser.is_existing ? switchToNewUser() : switchToExistingUser(existingUserMatch ? existingUserMatch : null))} name="is_existing" color="primary" />}
                        label="Existing User"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography
                        style={{
                            textTransform: 'uppercase',
                            marginLeft: '0.75rem',
                            fontSize: '0.75rem',
                            marginTop: '1rem',
                        }}
                    >
                        General user information
                    </Typography>
                </Grid>
                {tripUser.is_existing ? (
                    <React.Fragment>
                        <Grid item xs={12}>
                            <UserRemoteAutocomplete
                                textFieldProps={{ ...errors.user, error: clientErrors.userFoundBespoke && true, helperText: clientErrors.userFoundBespoke && ERROR_MESSAGES['userFoundBespoke'], autoFocus: true }}
                                getOptionLabel={(option) => option.email}
                                filterOptions={(options, state) => options}
                                resource={canBrowseUsers ? 'user' : 'tour_user'}
                                urlSuffix={canBrowseUsers ? null : 'lookup_existing/'}
                                apiParams={canBrowseUsers ? undefined : { tour_id: props.trip.id }}
                                label={canBrowseUsers ? 'User' : 'User email'}
                                variant="filled"
                                size="small"
                                onChange={onExistingUserSelect}
                                setClientErrors={setClientErrors}
                                query={tripUser.user.email}
                                value={tripUser.user}
                                onBlur={(ev) => handleUserRemoteAutoCompleteOnBlur(ev, tripUser)}
                                onQueryChange={(query) => setTripUser((prev) => ({ ...prev, user: { ...prev.user, email: query } }))}
                            />
                            {clientErrors.userNotFound && (
                                <Typography color="secondary" style={{ fontSize: '85%', marginLeft: '0.5rem' }}>
                                    User not found.{' '}
                                    <Link onClick={switchToNewUser} style={{ cursor: 'pointer' }}>
                                        Add new trip user.
                                    </Link>
                                </Typography>
                            )}
                        </Grid>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Grid item xs={12}>
                            <TextField
                                variant="filled"
                                size="small"
                                name="email"
                                label="Email"
                                value={tripUser.user.email}
                                onChange={onStringChange}
                                fullWidth
                                autoFocus
                                onBlur={onStringBlur}
                                required
                                InputProps={{
                                    endAdornment: serverCheck ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} color="primary" />
                                        </InputAdornment>
                                    ) : null,
                                }}
                                error={clientErrors.validEmailAddress || clientErrors.unicodeCharactersFound}
                                helperText={(clientErrors.validEmailAddress && ERROR_MESSAGES['validEmailAddress']) || (clientErrors.unicodeCharactersFound && ERROR_MESSAGES['unicodeCharactersFound'])}
                                {...errors.email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="filled" size="small" name="first_name" label="First Name" value={tripUser.user.first_name} onChange={onStringChange} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="filled" size="small" name="last_name" label="Last Name" value={tripUser.user.last_name} onChange={onStringChange} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <MaterialUiPhoneNumber
                                variant="filled"
                                size="small"
                                name="phone"
                                fullWidth
                                defaultCountry="us"
                                onChange={onPhoneChange}
                                value={tripUser.user.phone}
                                label="Phone"
                                InputProps={{
                                    endAdornment: phoneServerCheck ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} color="primary" />
                                        </InputAdornment>
                                    ) : null,
                                }}
                                    {...serverErrors?.phone}

                            />
                        </Grid>
                    </React.Fragment>
                )}
                <Grid item xs={12}>
                    <Typography
                        style={{
                            textTransform: 'uppercase',
                            marginLeft: '0.75rem',
                            fontSize: '0.75rem',
                            marginTop: '2rem',
                        }}
                    >
                        tour specific information
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        options={mapped_user_roles(tripUser, roleMap).length > 0 ? [...mapped_user_roles(tripUser, roleMap)] : [{ name: 'Guest', id: null }]}
                        getOptionLabel={(option) => {
                            return option.name;
                        }}
                        disabled={tripUser.user.roles?.length < 2}
                        value={tripUser.role ? roleMap[tripUser.role] : { name: 'Guest', id: null }}
                        onChange={onRoleChange}
                        renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="Role" />}
                    />
                </Grid>
                {!props.isSmallScreen && <Grid item md={6}></Grid>}
                {!props.trip.is_bespoke ? (
                    <>
                        <Grid item xs={requiresDepartureSelection ? 6 : 12} md={6}>
                            <DatePicker
                                value={tripUser.date ? moment.utc(tripUser.date) : null}
                                onChange={onDateChange}
                                disabled={props.syncActive}
                                format={'LL'}
                                label="Trip Date"
                                minDate={nonBespokeStartDate ? moment.utc(nonBespokeStartDate) : null}
                                maxDate={nonBespokeEndDate ? moment.utc(nonBespokeEndDate) : null}
                                error={clientErrors.sameDateExists}
                                slotProps={{
                                    textField: {
                                        variant: 'filled',
                                        size: 'small',
                                    },
                                }}
                            />
                        </Grid>
                        {!requiresDepartureSelection && !props.isSmallScreen && <Grid item md={6}></Grid>}
                        {requiresDepartureSelection ? (
                            <Grid item xs={6}>
                                <Autocomplete
                                    options={departureOptions}
                                    getOptionLabel={(option) => {
                                        return formatTimeValue(option.tag?.name) || '';
                                    }}
                                    value={tripUser.departure}
                                    disabled={!tripUser.date}
                                    onChange={onDepartureChange}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="filled"
                                            size="small"
                                            color="primary"
                                            label="Select departure"
                                            required={true}
                                            error={noDepartureTimes}
                                            helperText={noDepartureTimes ? 'This trip has no departure times. Add one under Trip Departures on the Trip Info tab first.' : undefined}
                                        />
                                    )}
                                />
                            </Grid>
                        ) : null}
                        {clientErrors.sameDateExists && (
                            <Grid item xs={12}>
                                <Typography color="secondary" style={{ fontSize: '0.85rem', marginLeft: '1rem', marginTop: '-0.75rem' }}>
                                    User is already registered {requiresDepartureSelection ? 'on this date and time' : 'on this date'}
                                </Typography>
                            </Grid>
                        )}
                    </>
                ) : props.trip.is_staff_chat_group ? null : (
                    /* CP-8349: no (synthetic) start date shown when adding to a guide group */
                    <>
                        <Grid item xs={12} md={6}>
                            <DatePicker
                                required
                                showTodayButton
                                autoOk
                                todayLabel="today"
                                label="Start Date"
                                value={moment(props.trip.start_date)}
                                format="MMMM D, YYYY"
                                onChange={(ev) => console.log(ev)}
                                disabled={true}
                                fullWidth
                                slotProps={{
                                    textField: {
                                        variant: 'filled',
                                        size: 'small',
                                    },
                                }}
                            />
                        </Grid>
                        {!props.isSmallScreen && <Grid item md={6}></Grid>}
                    </>
                )}
                <Grid item xs={12}>
                    <FormControlLabel
                        disabled={props.syncActive || !hasWritePerm}
                        title="Give this booking its own check-out time instead of deriving it from the tour length"
                        control={<Checkbox
                            color="primary"
                            name="own_end"
                            checked={tripUser.end_ts_type === BOOKING_OWN_END}
                            onChange={onOwnEndToggle} />}
                        label="This booking has its own check-out time" />
                </Grid>
                {tripUser.end_ts_type === BOOKING_OWN_END && (
                    <Grid item xs={12}>
                        <CheckoutPicker
                            timeZone={tripTimeZone}
                            tripDate={tripUser.date}
                            value={tripUser.end_ts}
                            onChange={onEndTsChange}
                            disabled={props.syncActive || !hasWritePerm}
                            textFieldProps={{ size: 'small' }}
                        />
                    </Grid>
                )}
                {tripUser.tags
                    ?.sort(compare)
                    ?.filter((tag) => tag.category?.code !== 'departure')
                    .map((tag, idx) => {
                        return (
                            <Grid item xs={12} container alignItems="center" key={idx}>
                                <Grid item xs={4} style={{ paddingRight: '0.1rem' }}>
                                    <Autocomplete
                                        options={allowedTagCategories}
                                        getOptionLabel={(option) => {
                                            return option.category?.name || '';
                                        }}
                                        value={tag}
                                        disabled={checkIfRequiredAndMandatory(tag)}
                                        onChange={(ev, cat) => handleTagCategoryChange(ev, cat, tag.idx)}
                                        renderInput={(params) => <TextField {...params} variant="filled" size="small" color="primary" label="Category" required={checkIfRequiredAndMandatory(tag)} />}
                                    />
                                </Grid>
                                {tag.category && (
                                    <Grid item xs={!checkIfRequiredAndMandatory(tag) ? 7 : 8}>
<RemoteAutocomplete
                                        apiFilters={[
                                            { field: 'category', op: 'eq', value: [tag.category?.id?.toString()] },
                                            { field: 'exclude_inactive_users', value: true }
                                        ]}
                                            value={tag}
                                            resource="tour_tag"
                                            urlSuffix={null}
                                            label="Select Tag"
                                            disableClearable={checkIfRequiredAndMandatory(tag)}
                                            required={checkIfRequiredAndMandatory(tag)}
                                            variant="filled"
                                            size="small"
                                            onChange={(ev, nt) => handleTagChange(ev, nt, tag.idx)}
                                            style={{ borderBottom: tag.error && '#fa3f33 solid 1px' }}
                                        />
                                    </Grid>
                                )}
                                {!checkIfRequiredAndMandatory(tag) ? (
                                    <Grid item xs={1} container justifyContent="flex-end">
                                        <DeleteIconButton title="Remove tag" fontSize="medium" sx={{ p: 0 }} onClick={(ev) => removeTagFromUser(ev, tag.idx)} />
                                    </Grid>
                                ) : null}
                            </Grid>
                        );
                    })}

                {allowedTagCategories.length > 0 && (
                    <Grid item xs={12}>
                        <Button color="primary" variant="outlined" onClick={addNewEmptyTag}>
                            Add tag
                        </Button>
                    </Grid>
                )}

                {(clientErrors.missingRequiredTags || clientErrors.missingDepartureTag) && (
                    <Grid item xs={12}>
                        <Typography color="secondary" style={{ margin: '1rem' }}>
                            Please add all required tags.
                        </Typography>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <TextField variant="filled" size="small" fullWidth name="photo_sharing" label="Photo Sharing URL (optional)" value={tripUser.tag_context?.photo_sharing || ''} onChange={onPhotoSharingLinkChange} />
                </Grid>

                <Grid item xs={12} mt={2}>
                    <Typography fontSize={16} fontWeight={500}>
                        Order
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        name="order_ref"
                        label="Order #"
                        variant="filled"
                        size="small"
                        fullWidth
                        value={tripUser.order_ref || ''}
                        onChange={onTextChange}
                    />
                </Grid>
                {/* The operator's code for the departure INSTANCE -- distinct from the
                    time-of-day departure tag. Two departures of one tour can share a
                    date and time and differ only here. */}
                <Grid item xs={12}>
                    <TextField
                        name="departure_key"
                        label="Departure code"
                        variant="filled"
                        size="small"
                        fullWidth
                        value={tripUser.departure_key || ''}
                        onChange={onTextChange}
                    />
                </Grid>

                <Grid item xs={12} mt={2}>
                    <Typography fontSize={16} fontWeight={500}>
                        Airbnb (if applicable)
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        name="airbnb_direct_user_thread_id"
                        label="Airbnb Direct User Thread ID"
                        variant="filled"
                        size="small"
                        fullWidth
                        value={tripUser.airbnb_direct_user_thread_id || ''}
                        onChange={onTextChange}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        name="airbnb_channel_id"
                        label="Airbnb Thread ID"
                        variant="filled"
                        size="small"
                        fullWidth
                        value={tripUser.airbnb_channel_id || ''}
                        onChange={onTextChange}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        name="airbnb_host_id"
                        label="Airbnb Host Account ID"
                        variant="filled"
                        size="small"
                        fullWidth
                        value={tripUser.airbnb_host_id || ''}
                        onChange={onTextChange}
                    />
                </Grid>
                <Grid item xs={12} mt={2}>
                    <Typography fontSize={16} fontWeight={500}>
                        Voucher
                    </Typography>
                </Grid>
                <input {...voucherDz.getInputProps()} />
                { tripUser.voucher && (
                    <Grid item xs={12}>
                        {/* CP-5559: the shared file row (name opens the file, Replace, trash) instead of [MODIFY VOUCHER]. */}
                        <FileActionRow
                            name={tripUser.voucher.name}
                            file={tripUser.voucher.file}
                            onReplace={() => handleOnReplace(voucherDz)}
                            onDelete={() => handleVoucherDelete()}
                            replaceTitle="Replace voucher"
                            deleteTitle="Remove voucher"
                            disabled={!hasWritePerm}
                        />
                    </Grid> )}
                    {hasWritePerm && !tripUser.voucher && <Grid item xs={12} color="primary">
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleOnReplace(voucherDz);
                            }}
                        >
                            <AddIcon fontSize="small"/>&nbsp;Add voucher
                        </Button>
                    </Grid>}

                <Grid item xs={12} mt={2}>
                    <Typography fontSize={16} fontWeight={500}>
                        Attachments
                    </Typography>
                </Grid>
                {tripUser.attachments?.map((file) => (
                    <Grid item xs={12} key={file.idx} style={{ backgroundColor: '#F5F5F5', height: '48px', borderRadius: '8px', display: 'flex', minWidth: 0 }} ml={2} mt={0.5} p={2} container justifyContent="space-between" alignItems="center" alignContent="center">
                        {/* CP-5559: Replace (blue) beside the trash; the name opens the picked file. */}
                        <FileActionRow
                            name={file.file?.name}
                            file={file.file}
                            onReplace={() => handleOnReplace(dz, file.idx)}
                            onDelete={() => handleFileDelete(file.idx)}
                            replaceTitle="Replace attachment"
                            deleteTitle="Remove attachment"
                            disabled={!hasWritePerm}
                            actionsAtEnd
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                ))}
                <Grid item xs={12} color="primary">
                    <input {...dz.getInputProps()} />
                    <Button
                        variant="outlined"
                        onClick={() => {
                            handleOnReplace(dz);
                        }}
                    >
                        <AddIcon fontSize="small"/>&nbsp;Add attachment
                    </Button>
                </Grid>
                {/* CP-5799: staged with the form, added after the user is (TripUserManager.saveChanges). */}
                <BookingTickets
                    tickets={[]}
                    changes={tripUser._ticketChanges}
                    onChangesChange={(next) => setTripUser((prev) => ({ ...prev, _ticketChanges: next, _changed: true }))}
                    canEdit={hasWritePerm}
                />
                {props.trip.has_group_chat &&
                <Grid item xs={12}>
                    <FormControlLabel disabled={!hasWritePerm} control={<Checkbox color="primary" name="exclude_from_group_chat" checked={tripUser.exclude_from_group_chat || tripUser.user.exclude_from_chat} onChange={onCheckboxChange} />} label="Exclude from group chat" />
                </Grid>
                }
                <Grid item xs={12}>
                    <FormActionButtons
                        submitRef={saveBtRef}
                        submitLabel="Add user"
                        onSubmit={onAddUser}
                        submitDisabled={disableSaveButton || props.syncActive || serverCheck || phoneServerCheck}
                        onCancel={onCancel}
                    />
                </Grid>
            </Grid>
        </div >
    );
});

AddTripUser.displayName = 'AddTripUser';

export default AddTripUser;
